import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React from 'react';

import {
  QUEUED_TOOL_DISPLAY_LABEL,
  SKIPPED_TOOL_DISPLAY_LABEL,
  ToolCallStatus,
} from 'llm/chat-library/progress/ProgressTypes';
import {getToolCallVisualOpacity} from 'llm/chat-library/progress/ToolCallVisualState';
import {ToolIconDisplay} from 'llm/chat-library/tool-details/ToolIconDisplay';
import {ToolIconSource} from 'llm/chat-library/tool-details/types';
import {LLMShimmerText} from 'llm/loading/LLMShimmer';
import {GRAY_DARKER} from 'ui-library/Colors';

import {agentProgressStyles} from './ChatProgress';
import {ThinkingDuotoneSpinner} from './ThinkingSpinner';

export interface ToolActivityItemProps {
  icon: ToolIconSource;
  displayName: string;
  status?: ToolCallStatus;
  isSkipped?: boolean;
  onClick?: () => void;
  testComponent: string;
  testKey?: string;
}

/**
 * Shared tool activity item for displaying a tool call in progress.
 * Shows an icon and the tool's display name.
 */
export function ToolActivityItem({
  icon,
  displayName,
  status = ToolCallStatus.UNKNOWN,
  isSkipped = false,
  onClick,
  testComponent,
  testKey,
}: ToolActivityItemProps): JSX.Element {
  const resolvedStatus = isSkipped ? ToolCallStatus.CANCELLED : status;
  const contentOpacity = getToolCallVisualOpacity(resolvedStatus);
  const resolvedDisplayName =
    resolvedStatus === ToolCallStatus.QUEUED
      ? `${displayName} ${QUEUED_TOOL_DISPLAY_LABEL}`
      : isSkipped && !displayName.endsWith(SKIPPED_TOOL_DISPLAY_LABEL)
        ? `${displayName} ${SKIPPED_TOOL_DISPLAY_LABEL}`
        : displayName;

  return (
    <div
      {...getTestProps({component: testComponent, element: 'activityItem', key: testKey})}
      className={css(agentProgressStyles.activityItem, onClick && agentProgressStyles.activityItemClickable)}
      onClick={onClick}
    >
      {status === ToolCallStatus.RUNNING ? (
        <div className={css(agentProgressStyles.activityExecutingIcon)}>
          <ThinkingDuotoneSpinner />
        </div>
      ) : (
        <ToolIconDisplay
          icon={icon}
          glyphClassName={css(agentProgressStyles.activityIcon)}
          imgClassName={css(agentProgressStyles.activityIconImg)}
          status={resolvedStatus}
          iconColor={GRAY_DARKER}
        />
      )}
      <div
        className={css(agentProgressStyles.activityDescription)}
        style={{opacity: contentOpacity}}
        {...getTestProps({
          component: testComponent,
          element: 'activityDescription',
          key: testKey,
        })}
      >
        <span
          className={css(agentProgressStyles.toolDescription)}
          {...getTestProps({
            component: testComponent,
            element: 'toolDescription',
            key: testKey,
          })}
        >
          {status === ToolCallStatus.RUNNING ? (
            <LLMShimmerText>{resolvedDisplayName}</LLMShimmerText>
          ) : (
            resolvedDisplayName
          )}
        </span>
      </div>
    </div>
  );
}
